import LogRocket from "logrocket";
import Bugsnag from "@bugsnag/js";
import { Capacitor } from "@capacitor/core";
import { ENVIRONMENTS } from "../config";

interface LogRocketConfig {
  appId: string;
  releaseStage?: string;
  errorOnly?: boolean;
  forceRecord?: boolean;
  apiBaseUrl?: string;
}

interface UserSubscription {
  name?: string;
  platform?: string;
  entriesPerMonth?: number;
  rewardPointsPerMonth?: number;
  dateSubscribed?: number;
}

type LogRocketTraits = Record<string, string | number | boolean>;

let isUserIdentified = false;
let isErrorOnlyMode = false;
let isForceRecordMode = false;
let hasErrorOccurred = false;
let configuredApiBaseUrl: string | undefined;
let lastUserTraits: LogRocketTraits | null = null;
let captureInterval: NodeJS.Timeout | null = null;
let visibilityChangeHandler: (() => void) | null = null;
let beforeUnloadHandler: (() => void) | null = null;
let pendingSessionCaptureTimeouts: Set<NodeJS.Timeout> = new Set();
let currentActiveUserId: string | null = null;
let capturedSessionIds: Set<string> = new Set();
let pendingSessionCaptures: Map<string, boolean> = new Map();

const isDevelopmentMode = (): boolean =>
  [
    ENVIRONMENTS.localProduction,
    ENVIRONMENTS.development,
    ENVIRONMENTS.local,
  ].includes(import.meta.env.MODE);

const shouldSkipLogRocketActions = (): boolean =>
  isDevelopmentMode() && !isForceRecordMode;

const isValidString = (value: unknown): value is string =>
  typeof value === "string" && value.trim() !== "";

const isValidTraitValue = (
  value: unknown,
): value is string | number | boolean =>
  typeof value === "string" ||
  typeof value === "number" ||
  typeof value === "boolean";

const buildSubscriptionTraits = (
  subscription: UserSubscription | null | undefined,
): LogRocketTraits => {
  if (!subscription) {
    return { hasSubscription: false };
  }

  const traits: LogRocketTraits = {};

  if (subscription.name) traits.subscriptionName = subscription.name;
  if (subscription.platform)
    traits.subscriptionPlatform = subscription.platform;
  if (typeof subscription.entriesPerMonth === "number")
    traits.subscriptionEntriesPerMonth = subscription.entriesPerMonth;
  if (typeof subscription.rewardPointsPerMonth === "number")
    traits.subscriptionRewardPointsPerMonth = subscription.rewardPointsPerMonth;
  if (typeof subscription.dateSubscribed === "number")
    traits.subscriptionDateSubscribed = subscription.dateSubscribed;

  return traits;
};

const buildMetadataTraits = (
  metadata: Record<string, unknown> | undefined,
): LogRocketTraits => {
  if (!metadata) return {};

  return Object.entries(metadata).reduce<LogRocketTraits>(
    (acc, [key, value]) => {
      if (isValidTraitValue(value)) {
        acc[key] = value;
      }
      return acc;
    },
    {},
  );
};

const setupGlobalErrorHandlers = (): (() => void) => {
  const handleError = (): void => {
    hasErrorOccurred = true;
  };

  const handleUnhandledRejection = (): void => {
    hasErrorOccurred = true;
  };

  window.addEventListener("error", handleError);
  window.addEventListener("unhandledrejection", handleUnhandledRejection);

  return () => {
    window.removeEventListener("error", handleError);
    window.removeEventListener("unhandledrejection", handleUnhandledRejection);
  };
};

const isCapacitorNative = (): boolean => {
  try {
    return typeof Capacitor !== "undefined" && Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

const transformCapacitorUrl = (url: string): string => {
  if (!isCapacitorNative()) return url;

  // Transform capacitor://localhost URLs to relative paths
  // This only affects LOCAL BUNDLED ASSETS (CSS, JS, images) in native apps
  // Production API URLs (https://...) are NEVER transformed - they remain unchanged
  //
  // Examples:
  // - capacitor://localhost/complete-profile -> /complete-profile (local route)
  // - capacitor://localhost/assets/style.css -> /assets/style.css (bundled asset)
  // - https://api.winnerslocker.com/endpoint -> unchanged (production API)
  if (url.startsWith("capacitor://localhost")) {
    return url.replace("capacitor://localhost", "");
  }

  // Also handle http://localhost for Android (development only)
  // Production Android apps use capacitor://localhost, not http://localhost
  if (url.startsWith("http://localhost")) {
    return url.replace("http://localhost", "");
  }

  // All other URLs (https://, production APIs, external resources) remain unchanged
  return url;
};

const setupCapacitorUrlTransformer = (): (() => void) => {
  if (!isCapacitorNative()) {
    return () => {};
  }

  // Transform capacitor://localhost URLs to relative paths in the DOM
  // This allows LogRocket to properly replay sessions by using relative paths
  // that work both in Capacitor (via <base href="/">) and in LogRocket's replay environment
  // Example: capacitor://localhost/complete-profile -> /complete-profile
  const transformElementUrls = (element: Element): void => {
    const attributesToTransform = [
      "href",
      "src",
      "action",
      "data-src",
      "srcset",
    ];

    attributesToTransform.forEach((attr) => {
      const value = element.getAttribute(attr);
      if (value) {
        const transformed = transformCapacitorUrl(value);
        if (transformed !== value) {
          element.setAttribute(attr, transformed);
        }
      }
    });

    // Handle srcset (can have multiple URLs)
    const srcset = element.getAttribute("srcset");
    if (srcset) {
      const transformed = srcset
        .split(",")
        .map((src) => {
          const parts = src.trim().split(/\s+/);
          if (parts[0]) {
            parts[0] = transformCapacitorUrl(parts[0]);
          }
          return parts.join(" ");
        })
        .join(", ");
      if (transformed !== srcset) {
        element.setAttribute("srcset", transformed);
      }
    }
  };

  // Transform existing DOM
  const transformExistingDOM = (): void => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", transformExistingDOM);
      return;
    }

    const allElements = document.querySelectorAll("*");
    allElements.forEach(transformElementUrls);
  };

  // Transform new elements as they're added
  const observer = new MutationObserver((mutations) => {
    try {
      mutations.forEach((mutation) => {
        try {
          mutation.addedNodes.forEach((node) => {
            try {
              if (node.nodeType === Node.ELEMENT_NODE) {
                transformElementUrls(node as Element);
                // Also transform child elements
                const children = (node as Element).querySelectorAll("*");
                children.forEach(transformElementUrls);
              }
            } catch (error) {
              console.log(
                "SILENT ERROR -- MutationObserver node transform",
                error,
              );
            }
          });

          // Transform attributes that changed
          if (
            mutation.type === "attributes" &&
            mutation.target.nodeType === Node.ELEMENT_NODE
          ) {
            try {
              transformElementUrls(mutation.target as Element);
            } catch (error) {
              console.log(
                "SILENT ERROR -- MutationObserver attribute transform",
                error,
              );
            }
          }
        } catch (error) {
          console.log(
            "SILENT ERROR -- MutationObserver mutation processing",
            error,
          );
        }
      });
    } catch (error) {
      console.log("SILENT ERROR -- MutationObserver", error);
    }
  });

  // Transform existing DOM
  transformExistingDOM();

  // Observe future changes
  const target = document.body || document.documentElement;
  if (target) {
    observer.observe(target, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["href", "src", "action", "data-src", "srcset"],
    });
  }

  return () => {
    observer.disconnect();
  };
};

export const initLogRocket = (config: LogRocketConfig): void => {
  isErrorOnlyMode = config.errorOnly === true;
  isForceRecordMode = config.forceRecord === true;
  configuredApiBaseUrl = config.apiBaseUrl;

  if (shouldSkipLogRocketActions()) return;

  if (isErrorOnlyMode && !isForceRecordMode) {
    setupGlobalErrorHandlers();
  }

  try {
    // Setup URL transformer for Capacitor before LogRocket initializes
    // This transforms capacitor://localhost URLs to relative paths so LogRocket
    // can properly fetch assets during session replay
    setupCapacitorUrlTransformer();

    LogRocket.init(config.appId, {
      release: config.releaseStage || import.meta.env.MODE,
      console: { isEnabled: true },
      network: { isEnabled: true },
      dom: { isEnabled: true },
      shouldDetectExceptions: true,
      shouldSendData: () => {
        // forceRecord (web sign-up debug) waits for identify so the session is
        // attributed to the signing-up user instead of being created anonymously.
        if (isForceRecordMode) {
          return isUserIdentified;
        }
        if (isErrorOnlyMode) {
          return hasErrorOccurred;
        }
        return isUserIdentified;
      },
    });

    if (
      typeof Bugsnag !== "undefined" &&
      typeof Bugsnag.getPlugin === "function"
    ) {
      LogRocket.getSessionURL((sessionURL: string) => {
        Bugsnag.addMetadata("session", { logrocketURL: sessionURL });
      });
    }
  } catch (error) {
    console.log("SILENT ERROR -- initLogRocket", error);
  }
};

export const identifyUser = (
  userId: string,
  email?: string,
  name?: string,
  phoneNumber?: string,
  metadata?: Record<string, unknown>,
  subscription?: UserSubscription | null,
): void => {
  if (shouldSkipLogRocketActions()) return;
  if (!isValidString(userId)) return;
  if (!isForceRecordMode && !email && !name) return;

  try {
    const traits: LogRocketTraits = {
      ...(email && { email }),
      ...(name && { name }),
      ...(phoneNumber && { phoneNumber }),
      ...buildSubscriptionTraits(subscription),
      ...buildMetadataTraits(metadata),
    };

    isUserIdentified = true;
    currentActiveUserId = userId;
    lastUserTraits = traits;
    LogRocket.identify(userId, traits);

    // Web sign-up debug (forceRecord) defers persistence until the user is
    // authenticated post-signup (see saveWebSignupLogRocketSession), since a
    // brand-new signup has no valid token yet. App users save immediately.
    if (!isForceRecordMode) {
      captureSessionUrl(userId);
      setupSessionCapture(userId);
    }
  } catch (error) {
    console.log("SILENT ERROR -- identifyUser", error);
  }
};

/**
 * Captures the current LogRocket session URL and saves it to Firestore.
 * This is called after identifying a user to track their session.
 * Uses POST request from client-side to our backend API.
 *
 * @param userId - The user ID to associate with the session
 * @param baseUrl - Optional base URL for the API endpoint. If not provided, will try to detect from environment.
 * @param retryCount - Current retry attempt (internal use, defaults to 0)
 * @param maxRetries - Maximum number of retry attempts (defaults to 10, which is 20 seconds total)
 */
const captureSessionUrl = (
  userId: string,
  baseUrl?: string,
  retryCount: number = 0,
  maxRetries: number = 10,
): void => {
  if (shouldSkipLogRocketActions()) return;

  if (!isUserIdentified) {
    return;
  }

  try {
    LogRocket.getSessionURL(async (sessionURL: string) => {
      if (!isUserIdentified) {
        return;
      }

      if (userId !== currentActiveUserId) {
        return;
      }

      if (!sessionURL) {
        if (retryCount >= maxRetries) {
          console.log(
            "SILENT ERROR -- captureSessionUrl: Max retries reached, LogRocket may not be initialized",
          );
          return;
        }

        const timeoutId = setTimeout(() => {
          pendingSessionCaptureTimeouts.delete(timeoutId);
          captureSessionUrl(userId, baseUrl, retryCount + 1, maxRetries);
        }, 2000);
        pendingSessionCaptureTimeouts.add(timeoutId);
        return;
      }

      if (!isUserIdentified) {
        return;
      }

      if (userId !== currentActiveUserId) {
        return;
      }

      // Capture traits synchronously before any async operations to prevent race condition
      // where lastUserTraits could be overwritten by a new user login during async operations
      const capturedTraits = lastUserTraits;

      try {
        const sessionId =
          sessionURL.split("/").pop() || `session-${Date.now()}`;

        if (
          capturedSessionIds.has(sessionId) ||
          pendingSessionCaptures.get(sessionId)
        ) {
          return;
        }

        const platform =
          typeof navigator !== "undefined" ? navigator.platform : undefined;
        const userAgent =
          typeof navigator !== "undefined" ? navigator.userAgent : undefined;

        let apiBaseUrl = baseUrl || configuredApiBaseUrl;
        if (!apiBaseUrl) {
          try {
            const { cloudFunctionsUrl } = await import(
              "../../AppUser/firebase"
            );
            apiBaseUrl = cloudFunctionsUrl;
          } catch {
            console.log("SILENT ERROR -- Could not determine API base URL");
            return;
          }
        }

        if (!apiBaseUrl) return;

        if (!isUserIdentified) {
          return;
        }

        if (userId !== currentActiveUserId) {
          return;
        }

        pendingSessionCaptures.set(sessionId, true);

        try {
          const { doPost, getRequestHeaders } = await import("./https");
          const headers = await getRequestHeaders(true);

          await doPost(`${apiBaseUrl}logrocket/sessions`, headers, {
            userId,
            sessionId,
            sessionUrl: sessionURL,
            startTime: Date.now(),
            platform,
            userAgent,
            traits: capturedTraits || undefined,
          });
          capturedSessionIds.add(sessionId);
        } finally {
          pendingSessionCaptures.delete(sessionId);
        }
      } catch (error) {
        console.log("SILENT ERROR -- captureSessionUrl", error);
      }
    });
  } catch (error) {
    console.log("SILENT ERROR -- captureSessionUrl", error);
  }
};

/**
 * Cleans up session capture resources (interval, event listeners, and pending timeouts).
 * This should be called when resetting the user or before setting up new session capture.
 */
const cleanupSessionCapture = (): void => {
  if (captureInterval) {
    clearInterval(captureInterval);
    captureInterval = null;
  }

  pendingSessionCaptureTimeouts.forEach((timeoutId) => {
    clearTimeout(timeoutId);
  });
  pendingSessionCaptureTimeouts.clear();

  if (visibilityChangeHandler && typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", visibilityChangeHandler);
    visibilityChangeHandler = null;
  }

  if (beforeUnloadHandler && typeof window !== "undefined") {
    window.removeEventListener("beforeunload", beforeUnloadHandler);
    beforeUnloadHandler = null;
  }
};

const cleanupSessionTracking = (): void => {
  capturedSessionIds.clear();
  pendingSessionCaptures.clear();
};

/**
 * Sets up periodic session capture to catch sessions that start after initial identify.
 * Also captures on page visibility changes (user returns to tab).
 *
 * @param userId - The user ID to associate with sessions
 */
const setupSessionCapture = (userId: string): void => {
  if (shouldSkipLogRocketActions()) return;
  if (typeof window === "undefined") return;

  cleanupSessionCapture();
  cleanupSessionTracking();

  let lastCapturedSessionId: string | null = null;

  const attemptCapture = () => {
    if (!isUserIdentified) {
      return;
    }

    if (userId !== currentActiveUserId) {
      return;
    }

    try {
      LogRocket.getSessionURL((sessionURL: string) => {
        if (!isUserIdentified) {
          return;
        }

        if (userId !== currentActiveUserId) {
          return;
        }

        if (!sessionURL) return;

        const sessionId = sessionURL.split("/").pop() || "";
        if (sessionId && sessionId !== lastCapturedSessionId) {
          lastCapturedSessionId = sessionId;
          captureSessionUrl(userId);
        }
      });
    } catch (error) {
      console.log("SILENT ERROR -- setupSessionCapture", error);
    }
  };

  if (typeof document !== "undefined") {
    visibilityChangeHandler = () => {
      if (!document.hidden) {
        const timeoutId = setTimeout(() => {
          pendingSessionCaptureTimeouts.delete(timeoutId);
          attemptCapture();
        }, 1000);
        pendingSessionCaptureTimeouts.add(timeoutId);
      }
    };
    document.addEventListener("visibilitychange", visibilityChangeHandler);
  }

  captureInterval = setInterval(attemptCapture, 30000);

  if (typeof window !== "undefined") {
    beforeUnloadHandler = () => {
      cleanupSessionCapture();
    };
    window.addEventListener("beforeunload", beforeUnloadHandler);
  }
};

/**
 * Ends the current LogRocket session and starts a brand-new one.
 * Used to isolate each web sign-up attempt so consecutive attempts in the same
 * tab become separate session records instead of re-tagging the previous one.
 */
export const startFreshLogRocketSession = (): void => {
  if (shouldSkipLogRocketActions()) return;

  try {
    cleanupSessionCapture();
    cleanupSessionTracking();
    isUserIdentified = false;
    currentActiveUserId = null;
    hasErrorOccurred = false;
    lastUserTraits = null;
    LogRocket.startNewSession();
  } catch (error) {
    console.log("SILENT ERROR -- startFreshLogRocketSession", error);
  }
};

export const resetUser = (): void => {
  startFreshLogRocketSession();
};

/**
 * Resolves the LogRocket session replay URL via a retry-until-ready loop, then
 * invokes onSessionUrl exactly once. Mirrors the app's captureSessionUrl retry
 * pattern so a freshly started/identified session has time to register without
 * depending on a single fixed timeout. Non-blocking; safe to fire-and-forget.
 * Web sign-up uses this AFTER create (decoupled from the signup request) to
 * persist the replay for the brand-new, not-yet-authenticated user.
 *
 * Exactly one of `onSessionUrl` / `onExhausted` fires. `onExhausted` runs when
 * the loop gives up (skipped, retries exhausted, or capture threw) so callers
 * can release any in-flight guard even when a URL never arrives.
 */
export const captureLogRocketSessionUrlWithRetry = (
  onSessionUrl: (sessionUrl: string) => void,
  options: {
    maxRetries?: number;
    intervalMs?: number;
    onExhausted?: () => void;
  } = {},
): void => {
  const { maxRetries = 15, intervalMs = 2000, onExhausted } = options;

  let settled = false;

  const finishWithUrl = (sessionUrl: string): void => {
    if (settled) return;
    settled = true;
    onSessionUrl(sessionUrl);
  };

  const giveUp = (): void => {
    if (settled) return;
    settled = true;
    onExhausted?.();
  };

  if (shouldSkipLogRocketActions()) {
    giveUp();
    return;
  }

  let attempts = 0;

  const attemptCapture = (): void => {
    try {
      LogRocket.getSessionURL((sessionURL: string) => {
        if (sessionURL) {
          finishWithUrl(sessionURL);
          return;
        }
        if (attempts >= maxRetries) {
          giveUp();
          return;
        }
        attempts += 1;
        setTimeout(attemptCapture, intervalMs);
      });
    } catch {
      // recording may not be initialized; stop retrying and release callers
      giveUp();
    }
  };

  attemptCapture();
};
